import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../lib/translations';

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const { language } = useLanguage();

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage(t('invalidVerificationLink', language));
      return;
    }

    const verifyEmail = async () => {
      try {
        await api.verifyEmail(token);
        setStatus('success');
        setMessage(t('emailVerifiedSuccess', language));
        // Redirect to login after 3 seconds
        setTimeout(() => navigate('/login'), 3000);
      } catch (err: any) {
        setStatus('error');
        setMessage(err.message || t('verificationFailed', language));
      }
    };

    verifyEmail();
  }, [token, navigate, language]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card card-large w-full max-w-md fade-in text-center">
        <div className="mb-6">
          <h1 className="text-3xl mb-2" style={{ color: 'var(--primary)' }}>
            MindsetFamily
          </h1>
        </div>

        {status === 'loading' && (
          <div className="py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('verifyingEmail', language)}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="py-8">
            <span className="text-5xl block mb-4">✅</span>
            <h2 className="text-xl font-semibold text-green-700 mb-2">{t('emailVerified', language)}</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500">{t('redirectingToLogin', language)}</p>
          </div>
        )}

        {status === 'error' && (
          <div className="py-8">
            <span className="text-5xl block mb-4">❌</span>
            <h2 className="text-xl font-semibold text-red-700 mb-2">{t('verificationFailed', language)}</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <Link to="/login" className="btn btn-primary">
              {t('goToLogin', language)}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
